/**
 * Owner Order Audit Page
 * 
 * Admin page showing all owner orders with staff who entered them.
 * Allows filtering by date range and viewing daily summaries.
 */

import { useState, useEffect, useCallback } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { 
  Crown, 
  Calendar, 
  RefreshCw, 
  Mail, 
  Download,
  ChevronDown,
  ChevronUp,
  User,
  Clock,
  DollarSign,
  Coffee,
  UtensilsCrossed,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

export default function OwnerOrderAuditPage() {
  const [orders, setOrders] = useState([]);
  const [dailySummary, setDailySummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  
  // Date filters - default to last 7 days
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Stats
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalValue: 0,
    todayOrders: 0,
    todayValue: 0
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      params.append('limit', '500');
      
      const response = await api.get(`/api/orders/owner/history?${params.toString()}`);
      const data = response.data;
      
      setOrders(data.orders || []);
      setDailySummary(data.daily_summary || {});
      
      // Calculate stats
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayOrders = (data.orders || []).filter(o => o.created_at?.startsWith(today));
      
      setStats({
        totalOrders: data.count || 0,
        totalValue: data.total_would_have_cost || 0,
        todayOrders: todayOrders.length,
        todayValue: todayOrders.reduce((sum, o) => sum + (o.original_total || 0), 0)
      });
      
    } catch (error) {
      console.error('Error fetching owner orders:', error);
      toast.error('Failed to load owner orders');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const sendDailyEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await api.post('/api/orders/owner/send-daily-email');
      if (response.data.success) {
        toast.success(`Email sent! ${response.data.order_count} orders, $${response.data.total_value?.toFixed(2)} value`);
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleOrderExpand = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Order #', 'Staff', 'Items', 'Would Have Cost'];
    const rows = orders.map(order => {
      const date = order.created_at ? new Date(order.created_at) : new Date();
      const items = (order.items || []).map(i => `${i.quantity}x ${i.name}`).join('; ');
      return [
        format(date, 'yyyy-MM-dd'),
        format(date, 'HH:mm'),
        order.order_number,
        order.staff_name || 'Unknown',
        `"${items}"`,
        (order.original_total || 0).toFixed(2)
      ];
    });
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `owner-orders-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'h:mm a');
    } catch {
      return 'N/A';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'EEE, MMM d');
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6" data-testid="owner-audit-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 dark:bg-teal-900 rounded-xl">
            <Crown className="w-8 h-8 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Owner Order Audit</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Track all owner orders and staff activity</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOrders}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={orders.length === 0}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            size="sm"
            onClick={sendDailyEmail}
            disabled={sendingEmail}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="send-email-btn"
          >
            <Mail className={`w-4 h-4 mr-2 ${sendingEmail ? 'animate-pulse' : ''}`} />
            {sendingEmail ? 'Sending...' : 'Send Email Now'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900 rounded-lg">
                <Crown className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today&apos;s Orders</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today&apos;s Value</p>
                <p className="text-2xl font-bold text-red-600">${stats.todayValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Period Orders</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Period Value</p>
                <p className="text-2xl font-bold text-amber-600">${stats.totalValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">From:</span>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="start-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">To:</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="end-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(format(new Date(), 'yyyy-MM-dd'));
                  setEndDate(format(new Date(), 'yyyy-MM-dd'));
                }}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
                  setEndDate(format(new Date(), 'yyyy-MM-dd'));
                }}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
                  setEndDate(format(new Date(), 'yyyy-MM-dd'));
                }}
              >
                Last 30 Days
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5" />
            Owner Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Crown className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-2 text-gray-500">No owner orders in this period</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div 
                  key={order.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  data-testid={`order-${order.order_number}`}
                >
                  {/* Order Header */}
                  <div 
                    className="p-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleOrderExpand(order.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-teal-600" />
                        <span className="font-bold text-teal-600">#{order.order_number}</span>
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {formatDate(order.created_at)} at {formatTime(order.created_at)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {order.staff_name || 'Unknown Staff'}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-red-600 font-bold">${(order.original_total || 0).toFixed(2)}</span>
                      {expandedOrders.has(order.id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Order Items (Expanded) */}
                  {expandedOrders.has(order.id) && (
                    <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Items:</h4>
                      <ul className="space-y-1">
                        {(order.items || []).map((item, idx) => (
                          <li key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {item.quantity}x {item.name}
                              {item.size && <span className="text-gray-400"> ({item.size})</span>}
                            </span>
                            <span className="text-gray-500">${(item.price || 0).toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                      {order.source && (
                        <p className="mt-3 text-xs text-gray-400">Source: {order.source}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Summary */}
      {Object.keys(dailySummary).length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {Object.entries(dailySummary)
                .sort((a, b) => b[0].localeCompare(a[0]))
                .map(([date, data]) => (
                  <div 
                    key={date}
                    className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center"
                  >
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {format(new Date(date), 'EEE, MMM d')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{data.count}</p>
                    <p className="text-sm text-red-600">${data.total_value.toFixed(2)}</p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
